import React, { useState, useMemo, useEffect } from 'react';
import { useData } from '../../context/DataContext';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { NotificationContainer } from '../../components/ui/NotificationToast';
import { formatAssetStatus } from '../../utils/formatters';
import { supabase } from '../../lib/supabaseClient';
import { logActivity } from '../../utils/activityLogger';

const DepartmentAuditPage = () => {
    const { assets, currentUser, isLoading } = useData();
    const [notifications, setNotifications] = useState([]);
    const [auditSelections, setAuditSelections] = useState({});
    const [isVerifying, setIsVerifying] = useState(false);
    const [isAuditedThisMonth, setIsAuditedThisMonth] = useState(false);
    const [isCheckingStatus, setIsCheckingStatus] = useState(true);

    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [auditHistory, setAuditHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const [selectedReport, setSelectedReport] = useState(null);
    const [activeDetailTab, setActiveDetailTab] = useState('all');
    const [detailSearch, setDetailSearch] = useState('');

    const filteredDetailAssets = useMemo(() => {
        if (!selectedReport || !selectedReport.asset_details) return [];
        return selectedReport.asset_details.filter(asset => {
            const matchesTab = activeDetailTab === 'all' || asset.audited_condition === activeDetailTab;
            const matchesSearch = 
                asset.asset_tag.toLowerCase().includes(detailSearch.toLowerCase()) ||
                asset.product_name.toLowerCase().includes(detailSearch.toLowerCase());
            return matchesTab && matchesSearch;
        });
    }, [selectedReport, activeDetailTab, detailSearch]);

    const addNotification = (message, type) => {
        setNotifications(prev => [...prev, { id: Date.now(), message, type }]);
    };

    const fetchAuditHistory = async () => {
        setLoadingHistory(true);
        try {
            const { data, error } = await supabase
                .from('audit_logs')
                .select('*')
                .eq('department_id', currentUser.department_id)
                .order('created_at', { ascending: false });

            if (error) throw error;
            setAuditHistory(data || []);
            setIsHistoryOpen(true); // Open modal after data is ready
        } catch (error) {
            console.error("Error fetching history:", error);
            alert("Failed to load audit history.");
        } finally {
            setLoadingHistory(false);
        }
    };

    const checkMonthlyAuditStatus = async () => {
        if (!currentUser?.department_id) return;

        setIsCheckingStatus(true);
        try {
            const currentMonth = new Date().getMonth();
            const currentYear = new Date().getFullYear();

            // Get the most recent audit for this department
            const { data: latestAudit, error } = await supabase
                .from('audit_logs')
                .select('created_at')
                .eq('department_id', currentUser.department_id)
                .order('created_at', { ascending: false })
                .limit(1)
                .single();

            if (latestAudit) {
                const auditDate = new Date(latestAudit.created_at);
                if (auditDate.getMonth() === currentMonth && auditDate.getFullYear() === currentYear) {
                    setIsAuditedThisMonth(true);
                }
            }
        } catch (error) {
            // Note: .single() returns error if no rows found, which is fine
            if (error.code !== 'PGRST116') {
                console.error("Error checking audit status:", error);
            }
        } finally {
            setIsCheckingStatus(false);
        }
    };

    useEffect(() => {
        if (currentUser) {
            checkMonthlyAuditStatus();
        }
    }, [currentUser]);

    const progress = useMemo(() => {
        if (!assets.length) return 0;
        return (Object.keys(auditSelections).length / assets.length) * 100;
    }, [assets, auditSelections]);

    const isAuditComplete = useMemo(() => {
        return assets.length > 0 && Object.keys(auditSelections).length === assets.length;
    }, [assets, auditSelections]);

    const handleSelectCondition = (assetTag, condition) => {
        setAuditSelections(prev => ({
            ...prev,
            [assetTag]: condition
        }));
    };

    const handleAuditSubmit = async () => {
        if (!isAuditComplete) return;

        setIsVerifying(true);
        try {
            // 1. Tally up the results for the historical snapshot
            let goodCount = 0;
            let damagedCount = 0;
            let missingCount = 0;

            Object.values(auditSelections).forEach(status => {
                if (status === 'good') goodCount++;
                if (status === 'damaged') damagedCount++;
                if (status === 'missing') missingCount++;
            });

            // 2. Prepare asset updates & build historical audit details snapshot
            const assetDetailsList = [];
            const assetUpdates = assets.map(asset => {
                const auditStatus = auditSelections[asset.asset_tag];
                let dbCondition = 'Good';
                let dbStatus = asset.status;

                if (auditStatus === 'damaged') {
                    dbCondition = 'Requires Maintenance';
                } else if (auditStatus === 'missing') {
                    dbCondition = 'Lost/Missing';
                    dbStatus = 'Lost/Stolen';
                }

                const currentLoan = asset.loans?.find(l => l.status === 'active');
                const loanUserName = currentLoan?.employees?.full_name || 'In Storage';

                assetDetailsList.push({
                    asset_tag: asset.asset_tag,
                    product_name: asset.product_name,
                    audited_condition: auditStatus, // 'good', 'damaged', 'missing'
                    condition: dbCondition,
                    status: dbStatus,
                    current_user: loanUserName
                });

                return supabase
                    .from('assets')
                    .update({
                        condition: dbCondition,
                        status: dbStatus,
                        last_verified_at: new Date().toISOString()
                    })
                    .eq('asset_tag', asset.asset_tag);
            });

            // 3. Save the permanent historical report to audit_logs
            const auditLogInsert = supabase
                .from('audit_logs')
                .insert({
                    department_id: currentUser.department_id,
                    audited_by: currentUser.id,
                    total_assets: assets.length,
                    good_count: goodCount,
                    damaged_count: damagedCount,
                    missing_count: missingCount,
                    asset_details: assetDetailsList
                });

            // 4. Update department audit timestamp
            const departmentUpdate = supabase
                .from('departments')
                .update({
                    last_audit_date: new Date().toISOString(),
                    last_audit_by: currentUser.id
                })
                .eq('id', currentUser.department_id);

            // 5. Execute everything concurrently
            const results = await Promise.all([
                ...assetUpdates,
                auditLogInsert,
                departmentUpdate
            ]);

            const error = results.find(r => r.error)?.error;
            if (error) throw error;

            // Log activity
            await logActivity(
                'audit_completed',
                `Monthly audit completed for ${currentUser.department_name}. Summary: ${goodCount} Good, ${damagedCount} Damaged, ${missingCount} Missing.`,
                null,
                currentUser.id
            );

            addNotification("Monthly audit and historical snapshot successfully saved!", "success");

            // Set audited state to trigger success UI
            setIsAuditedThisMonth(true);

            // Clear selections after successful submission
            setAuditSelections({});
        } catch (error) {
            console.error("Audit submission failed:", error);
            addNotification(`Audit submission failed: ${error.message}`, "error");
        } finally {
            setIsVerifying(false);
        }
    };

    if (isLoading || isCheckingStatus) return <div className="p-8 text-center text-muted-foreground">Loading audit data...</div>;

    return (
        <div className="space-y-6">
            <NotificationContainer
                notifications={notifications}
                onRemove={(id) => setNotifications(prev => prev.filter(n => n.id !== id))}
            />

            {isAuditedThisMonth ? (
                <div className="flex flex-col items-center justify-center p-12 bg-green-50 rounded-2xl border border-green-200 shadow-sm text-center">
                    <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mb-6 text-green-600 shadow-inner">
                        <Icon name="CheckCircle" size={40} />
                    </div>
                    <h2 className="text-3xl font-bold text-green-900 mb-3">Monthly Audit Complete</h2>
                    <p className="text-green-700 max-w-md text-lg font-medium leading-relaxed">
                        Thank you! Your department's asset audit for <strong>{new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}</strong> has been successfully recorded.
                    </p>
                    <div className="mt-8 flex gap-4">
                        <Button 
                            variant="outline" 
                            iconName="History" 
                            onClick={fetchAuditHistory}
                            disabled={loadingHistory}
                            loading={loadingHistory}
                        >
                            View Audit History
                        </Button>
                        <Button variant="secondary" iconName="LayoutDashboard" onClick={() => window.location.href = '/dashboard'}>
                            Back to Dashboard
                        </Button>
                    </div>
                </div>
            ) : (
                <>
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-foreground">{currentUser?.department_name} Monthly Audit</h1>
                            <p className="text-muted-foreground">Verify each asset to complete your monthly compliance check.</p>
                        </div>
                        <div className="flex items-center gap-4">
                            <Button 
                                variant="outline" 
                                iconName="History" 
                                onClick={fetchAuditHistory}
                                disabled={loadingHistory}
                                loading={loadingHistory}
                            >
                                View History
                            </Button>
                            <div className="text-right hidden sm:block">
                                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Audit Progress</p>
                                <p className="text-sm font-bold text-foreground">{Object.keys(auditSelections).length} of {assets.length} assets verified</p>
                            </div>
                            <Button
                                variant="primary"
                                iconName="Send"
                                onClick={handleAuditSubmit}
                                disabled={!isAuditComplete || isVerifying}
                                loading={isVerifying}
                            >
                                Submit Monthly Audit
                            </Button>
                        </div>
                    </div>

                    {/* Progress Bar Container */}
                    <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-bold text-foreground">Verification Progress</span>
                            <span className="text-sm font-bold text-primary">{Math.round(progress)}%</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                            <div
                                className="bg-primary h-full transition-all duration-500 ease-out rounded-full"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>

                    {/* Asset Verification List */}
                    <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-border bg-muted/50 flex justify-between items-center">
                            <h3 className="text-sm font-bold text-foreground">Department Hardware Inventory</h3>
                            <div className="flex gap-4 text-xs font-medium text-muted-foreground">
                                <span className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-green-500"></div> Good</span>
                                <span className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-amber-500"></div> Damaged</span>
                                <span className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-red-500"></div> Missing</span>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-border">
                                <thead className="bg-muted">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Asset Info</th>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Current User</th>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">System Status</th>
                                        <th className="px-6 py-3 text-center text-xs font-bold text-muted-foreground uppercase tracking-wider w-80">Audit Verification</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-card divide-y divide-border">
                                    {assets.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-12 text-center text-muted-foreground">
                                                No assets found for your department.
                                            </td>
                                        </tr>
                                    ) : (
                                        assets.map((asset) => {
                                            const selection = auditSelections[asset.asset_tag];
                                            const currentLoan = asset.loans?.find(l => l.status === 'active');

                                            return (
                                                <tr key={asset.asset_tag} className={`hover:bg-muted/80 transition-colors ${selection ? 'bg-primary/5' : ''}`}>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex flex-col">
                                                            <span className="text-sm font-bold text-[#0057B8]">{asset.asset_tag}</span>
                                                            <span className="text-xs text-foreground font-medium">{asset.product_name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                                                        {currentLoan?.employees?.full_name || 'In Storage'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold ${asset.status === 'In Use' ? 'bg-primary/20 text-primary' :
                                                                asset.status === 'Available' ? 'bg-green-100 text-green-700' :
                                                                    'bg-muted/50 text-foreground/80'
                                                            }`}>
                                                            {formatAssetStatus(asset.status)}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex justify-center gap-2">
                                                            <button
                                                                onClick={() => handleSelectCondition(asset.asset_tag, 'good')}
                                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${selection === 'good'
                                                                        ? 'bg-green-600 border-green-600 text-white shadow-md'
                                                                        : 'bg-card border-border text-muted-foreground hover:border-green-300 hover:text-green-600'
                                                                    }`}
                                                            >
                                                                <Icon name="CheckCircle" size={14} />
                                                                Good
                                                            </button>
                                                            <button
                                                                onClick={() => handleSelectCondition(asset.asset_tag, 'damaged')}
                                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${selection === 'damaged'
                                                                        ? 'bg-amber-500 border-amber-500 text-white shadow-md'
                                                                        : 'bg-card border-border text-muted-foreground hover:border-amber-300 hover:text-amber-600'
                                                                    }`}
                                                            >
                                                                <Icon name="AlertTriangle" size={14} />
                                                                Damaged
                                                            </button>
                                                            <button
                                                                onClick={() => handleSelectCondition(asset.asset_tag, 'missing')}
                                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${selection === 'missing'
                                                                        ? 'bg-red-600 border-red-600 text-white shadow-md'
                                                                        : 'bg-card border-border text-muted-foreground hover:border-red-300 hover:text-red-600'
                                                                    }`}
                                                            >
                                                                <Icon name="XCircle" size={14} />
                                                                Missing
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Empty State / Completion Call to Action */}
                    {isAuditComplete && !isVerifying && (
                        <div className="bg-primary/10 border border-primary/20 rounded-xl p-8 text-center space-y-4 animate-in fade-in zoom-in duration-300">
                            <div className="bg-primary w-16 h-16 rounded-full flex items-center justify-center mx-auto text-white shadow-lg">
                                <Icon name="ClipboardCheck" size={32} />
                            </div>
                            <div className="max-w-md mx-auto">
                                <h2 className="text-xl font-bold text-foreground">Audit Ready for Submission</h2>
                                <p className="text-muted-foreground text-sm">
                                    Excellent! You have verified all {assets.length} assets in your department.
                                    Click the button below to finalize this month's compliance audit.
                                </p>
                            </div>
                            <Button
                                variant="primary"
                                size="lg"
                                iconName="Send"
                                onClick={handleAuditSubmit}
                                loading={isVerifying}
                                className="px-12 rounded-full"
                            >
                                Submit Final Audit
                            </Button>
                        </div>
                    )}
                </>
            )}

            {/* Audit History Modal */}
            {isHistoryOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
                    <div className="bg-card rounded-xl border border-border shadow-2xl w-full max-w-4xl flex flex-col max-h-[90vh]">
                        {/* Header */}
                        <div className="flex items-center justify-between p-6 border-b border-border bg-card sticky top-0 z-10 rounded-t-xl">
                            <div>
                                <h2 className="text-xl font-bold text-foreground">Audit History</h2>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Historical snapshots for {currentUser?.department_name}
                                </p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => setIsHistoryOpen(false)}>
                                <Icon name="X" size={20} />
                            </Button>
                        </div>

                        {/* Content */}
                        <div className="p-6 overflow-y-auto">
                            {auditHistory.length === 0 ? (
                                <div className="text-center py-12 text-muted-foreground">
                                    No historical audit logs found for this department.
                                </div>
                            ) : (
                                <div className="border border-border rounded-lg overflow-hidden">
                                    <table className="min-w-full divide-y divide-border">
                                        <thead className="bg-muted">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Audit Date</th>
                                                <th className="px-6 py-3 text-center text-xs font-bold text-muted-foreground uppercase tracking-wider">Total Assets</th>
                                                <th className="px-6 py-3 text-center text-xs font-bold text-muted-foreground uppercase tracking-wider">Condition Breakdown</th>
                                                <th className="px-6 py-3 text-right text-xs font-bold text-muted-foreground uppercase tracking-wider">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-card divide-y divide-border">
                                            {auditHistory.map((log) => (
                                                <tr key={log.id} className="hover:bg-muted transition-colors">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                                                        {new Date(log.created_at).toLocaleString('default', { month: 'long', year: 'numeric' })}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center font-bold text-foreground">
                                                        {log.total_assets}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex justify-center gap-4">
                                                            <span className="flex items-center gap-1.5 text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded-md border border-green-100 font-medium">
                                                                <Icon name="CheckCircle" size={14} className="text-green-600" />
                                                                {log.good_count}
                                                            </span>
                                                            <span className="flex items-center gap-1.5 text-xs font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded-md border border-amber-100 font-medium">
                                                                <Icon name="AlertTriangle" size={14} className="text-amber-600" />
                                                                {log.damaged_count}
                                                            </span>
                                                            <span className="flex items-center gap-1.5 text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded-md border border-red-100 font-medium">
                                                                <Icon name="XCircle" size={14} className="text-red-600" />
                                                                {log.missing_count}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                                        <Button 
                                                            variant="ghost" 
                                                            size="xs" 
                                                            iconName="ExternalLink" 
                                                            onClick={() => setSelectedReport(log)}
                                                        >
                                                            Details
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="flex justify-end p-6 border-t border-border bg-muted rounded-b-xl">
                            <Button variant="outline" onClick={() => setIsHistoryOpen(false)}>
                                Close History
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Detail Modal */}
            {selectedReport && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[60] overflow-y-auto">
                    <div className="bg-card rounded-xl border border-border shadow-2xl w-full max-w-3xl flex flex-col max-h-[90vh]">
                        <div className="flex items-center justify-between p-6 border-b border-border bg-card sticky top-0 z-10 rounded-t-xl">
                            <div>
                                <h2 className="text-xl font-bold text-foreground">Audit Snapshot Details</h2>
                                <p className="text-sm text-muted-foreground mt-1">
                                    {currentUser?.department_name} - {new Date(selectedReport.created_at).toLocaleString('default', { month: 'long', year: 'numeric' })}
                                </p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => setSelectedReport(null)}>
                                <Icon name="X" size={20} />
                            </Button>
                        </div>
                        <div className="p-6 space-y-6 overflow-y-auto">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-muted p-4 rounded-lg">
                                    <p className="text-xs font-bold text-muted-foreground uppercase">Auditor</p>
                                    <p className="text-sm font-medium mt-1">{selectedReport.profiles?.full_name || currentUser?.full_name || 'N/A'}</p>
                                </div>
                                <div className="bg-muted p-4 rounded-lg">
                                    <p className="text-xs font-bold text-muted-foreground uppercase">Total Assets</p>
                                    <p className="text-sm font-medium mt-1">{selectedReport.total_assets}</p>
                                </div>
                            </div>
                            
                            <div className="space-y-3">
                                <h3 className="text-sm font-bold text-foreground uppercase tracking-wider">Condition Breakdown</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-100">
                                        <div className="flex items-center gap-2">
                                            <Icon name="CheckCircle" size={18} className="text-green-600" />
                                            <span className="text-sm font-medium text-green-900">Good</span>
                                        </div>
                                        <span className="font-bold text-green-600">{selectedReport.good_count}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg border border-amber-100">
                                        <div className="flex items-center gap-2">
                                            <Icon name="AlertTriangle" size={18} className="text-amber-600" />
                                            <span className="text-sm font-medium text-amber-900">Damaged</span>
                                        </div>
                                        <span className="font-bold text-amber-600">{selectedReport.damaged_count}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg border border-red-100">
                                        <div className="flex items-center gap-2">
                                            <Icon name="XCircle" size={18} className="text-red-600" />
                                            <span className="text-sm font-medium text-red-900">Missing</span>
                                        </div>
                                        <span className="font-bold text-red-600">{selectedReport.missing_count}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-4 border-t border-border">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-muted-foreground">Compliance Percentage</span>
                                    <span className="text-lg font-bold text-primary">
                                        {selectedReport.total_assets ? Math.round((selectedReport.good_count / selectedReport.total_assets) * 100) : 0}%
                                    </span>
                                </div>
                                <div className="w-full bg-muted rounded-full h-2 mt-2">
                                    <div 
                                        className="bg-primary h-full rounded-full transition-all" 
                                        style={{ width: `${selectedReport.total_assets ? (selectedReport.good_count / selectedReport.total_assets) * 100 : 0}%` }}
                                    />
                                </div>
                            </div>

                            {/* Detailed Asset List */}
                            <div className="space-y-4 pt-4 border-t border-border">
                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                                    <h3 className="text-sm font-bold text-foreground uppercase tracking-wider">Asset Details</h3>
                                    <div className="flex flex-wrap gap-1 bg-muted p-1 rounded-lg">
                                        {['all', 'good', 'damaged', 'missing'].map((tab) => {
                                            const count = tab === 'all' 
                                                ? selectedReport.asset_details?.length || 0
                                                : selectedReport.asset_details?.filter(a => a.audited_condition === tab).length || 0;
                                            return (
                                                <button
                                                    key={tab}
                                                    type="button"
                                                    onClick={() => setActiveDetailTab(tab)}
                                                    className={`px-2.5 py-1 rounded-md text-xs font-bold capitalize transition-all ${
                                                        activeDetailTab === tab
                                                            ? 'bg-card text-foreground shadow-sm'
                                                            : 'text-muted-foreground hover:text-foreground'
                                                    }`}
                                                >
                                                    {tab} ({count})
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="relative">
                                    <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                    <input
                                        type="text"
                                        placeholder="Search by tag or product..."
                                        value={detailSearch}
                                        onChange={(e) => setDetailSearch(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2 border border-border bg-card rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    />
                                </div>

                                <div className="border border-border rounded-lg overflow-hidden max-h-60 overflow-y-auto">
                                    {!selectedReport.asset_details ? (
                                        <div className="p-6 text-center text-sm text-muted-foreground">
                                            No asset details were recorded for this audit snapshot. Detailed breakdown is available for audits starting from May 2026.
                                        </div>
                                    ) : filteredDetailAssets.length === 0 ? (
                                        <div className="p-6 text-center text-sm text-muted-foreground">
                                            No matching assets found.
                                        </div>
                                    ) : (
                                        <table className="min-w-full divide-y divide-border text-left">
                                            <thead className="bg-muted text-xs font-bold text-muted-foreground uppercase tracking-wider sticky top-0">
                                                <tr>
                                                    <th className="px-4 py-2">Asset Tag</th>
                                                    <th className="px-4 py-2">Product Name</th>
                                                    <th className="px-4 py-2">Audited Condition</th>
                                                    <th className="px-4 py-2">Assigned User</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-card divide-y divide-border text-xs">
                                                {filteredDetailAssets.map((asset) => (
                                                    <tr key={asset.asset_tag} className="hover:bg-muted/50 transition-colors">
                                                        <td className="px-4 py-2.5 font-bold">
                                                            <a 
                                                                href={`/assets/${asset.asset_tag}`}
                                                                className="text-[#0057B8] hover:underline"
                                                                onClick={() => {
                                                                    setSelectedReport(null);
                                                                    setIsHistoryOpen(false);
                                                                }}
                                                            >
                                                                {asset.asset_tag}
                                                            </a>
                                                        </td>
                                                        <td className="px-4 py-2.5 font-medium text-foreground">{asset.product_name}</td>
                                                        <td className="px-4 py-2.5">
                                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-bold uppercase text-[9px] ${
                                                                asset.audited_condition === 'good' ? 'bg-green-100 text-green-700' :
                                                                asset.audited_condition === 'damaged' ? 'bg-amber-100 text-amber-700' :
                                                                'bg-red-100 text-red-700'
                                                            }`}>
                                                                {asset.audited_condition}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-2.5 text-muted-foreground">{asset.current_user}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end p-6 border-t border-border bg-muted rounded-b-xl">
                            <Button variant="outline" onClick={() => setSelectedReport(null)}>
                                Close Details
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DepartmentAuditPage;
